// ATLAS · TRAJECTORY — direction of travel, derived only from real drift data:
// project drift scores (tkneDrift, 0..4 per stage), today's drifting sectors, and the STAGE ladder's Aim window pace.
// There is no forecasting engine: no predictions or confidence are produced. "Improving" needs history we don't keep, so it is never claimed.
import { SECTOR_LABELS } from '../../../../domain/olmps';
import type { OrbWidgetModel, WidgetCtx } from '../types';
import { emptyModel, openAction } from './util';

const BASE = { id: 'oracle', toolId: 'atlas', orderFunction: 'orient', title: 'TRAJECTORY', deityName: 'ATLAS', agentId: 'oracle' } as const;

export function useOracleWidget(c: WidgetCtx): OrbWidgetModel {
  const all = (Object.values(c.projects ?? {}) as WidgetCtx['projects'][keyof WidgetCtx['projects']][]).flat();
  const scored = all.map((p) => ({ p, worst: Math.max(0, ...p.drift.map((d) => d.score)) }));
  const worst = scored.reduce<(typeof scored)[number] | null>((m, x) => (!m || x.worst > m.worst ? x : m), null);
  const driftSectors = c.today.driftSectors ?? [];

  if (!all.length && !driftSectors.length) {
    return emptyModel(BASE, c, 'No trajectory yet', { label: 'Open ATLAS', run: () => c.openTool('atlas'), tone: 'gold' });
  }

  const projDrifting = scored.filter((x) => x.worst >= 1);
  const drifting = projDrifting.length > 0 || driftSectors.length > 0;
  const lapsed = (worst?.worst ?? 0) >= 3;
  const driver = projDrifting.length
    ? `${worst!.p.name} (${SECTOR_LABELS[worst!.p.sector]})`
    : driftSectors[0] ?? null;

  const aimWin = c.today.ladder?.rungs?.aim?.currentWindow;
  const lines: string[] = [`Trajectory: ${drifting ? 'drifting' : 'stable'}`];
  if (driver) lines.push(`Main driver: ${driver}`);
  if (projDrifting.length) lines.push(`${projDrifting.length} of ${all.length} project${all.length === 1 ? '' : 's'} behind`);
  if (driftSectors.length) lines.push(`Overdue today: ${driftSectors.join(', ')}`);
  if (aimWin && aimWin.needed > 0) lines.push(`Aim window: ${aimWin.count}/${aimWin.needed} · ${aimWin.onTrack ? 'on track' : 'behind pace'}`);

  const summary = drifting ? 'Trajectory: drifting' : 'Trajectory: stable';
  return {
    ...BASE,
    status: lapsed ? 'critical' : drifting ? 'attention' : 'quiet',
    summary,
    alert: drifting && driver ? `Drifting: ${driver}` : null,
    lines,
    metrics: [
      ...(all.length ? [{ label: 'Projects behind', value: `${projDrifting.length}/${all.length}`, tone: projDrifting.length ? ('warning' as const) : ('positive' as const) }] : []),
      ...(aimWin && aimWin.needed > 0 ? [{ label: 'Aim window', value: `${aimWin.count}/${aimWin.needed}`, tone: aimWin.onTrack ? ('positive' as const) : ('warning' as const) }] : []),
    ],
    primaryAction: { label: 'Open ATLAS', run: () => c.openTool('atlas'), tone: 'gold' },
    secondaryActions: [],
    signals: { active: true, alerted: drifting, related: !!c.sector && scored.some((x) => x.p.sector === c.sector && x.worst >= 1) },
  };
}
